import {
  Component,
  Input,
  NgZone,
  OnChanges,
  OnInit,
  signal,
  SimpleChanges,
  ViewChild,
  WritableSignal
} from '@angular/core';
import { ListResource } from '@app/resources';
import { LMFacadeService } from '@components/facade/services';
import { LMBusyIndicatorService, LMErrorUtilService, LMFormatUtilService } from '@app/services';
import { SohoAccordionComponent } from 'ids-enterprise-ng';

@Component({
  selector:    'lm-facade-report-template-detail',
  standalone: false,
  templateUrl: './lm-facade-report-template-detail.component.html',
  styleUrl: './lm-facade-report-template-detail.component.scss',
})
export class LMFacadeReportTemplateDetailComponent implements OnInit, OnChanges {

  @Input() reportTemplateDetail: LMFacadeReportTemplateDetailRequest | undefined;
  @ViewChild(SohoAccordionComponent) accordionComponent: SohoAccordionComponent | undefined;

  private defaultTemplateDetail: LMFacadeReportTemplateDetail = {
    reportType: 'transactional',
    asOfDateData: false,
    archiveData: false,
    auditData: false,
    fields: [],
    listFilter: '',
    reportFilter: '',
  };

  displayTemplateDetail: WritableSignal<LMFacadeReportTemplateDetail> = signal(this.defaultTemplateDetail);
  asOfDateLabel: WritableSignal<string> = signal('');
  showFields: WritableSignal<boolean> = signal(false);
  showDimensions: WritableSignal<boolean> = signal(false);
  showMeasures: WritableSignal<boolean> = signal(false);
  showFilter: WritableSignal<boolean> = signal(false);
  showSort: WritableSignal<boolean> = signal(false);
  heightClass: WritableSignal<string> = signal('lm-two-panes');

  propertiesLabel = Soho.Locale.translate("CreateReportPropertiesTab");
  fieldsLabel= Soho.Locale.translate('Fields');
  dimensionsLabel = Soho.Locale.translate('Dimensions');
  measuresLabel = Soho.Locale.translate('Measures');
  filterLabel = Soho.Locale.translate('Filter');
  sortLabel= Soho.Locale.translate('DefaultSort');
  auditLabel = Soho.Locale.translate('Audit');
  archiveLabel= Soho.Locale.translate('Archive');
  cubeLabel = Soho.Locale.translate('Cube');
  columnarLabel= Soho.Locale.translate('Columnar');
  transactionalLabel = Soho.Locale.translate('Transactional');
  startReportLabel = Soho.Locale.translate('Facade_CopyReport');

  constructor(
    protected lmFacadeService: LMFacadeService,
    protected lmFormatUtilService: LMFormatUtilService,
    protected lmErrorUtilService: LMErrorUtilService,
    protected lmBusyIndicatorService: LMBusyIndicatorService,
    protected ngZone: NgZone
  ) {

  }

  ngOnInit(): void {
     this.initializeDetailContent();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.reportTemplateDetail && changes.reportTemplateDetail) {
      this.initializeDetailContent();
    }
  }

  private initializeDetailContent(): void {
    if (this.reportTemplateDetail) {
      const listResource = new ListResource({ resourceId: this.reportTemplateDetail.template.businessView + '.' + this.reportTemplateDetail.template.name });
      this.lmBusyIndicatorService.showBusyIndicator();
      this.lmFacadeService.getTemplateDetail(listResource, this.reportTemplateDetail.template.userFolder)
          .subscribe({
            next: (v)       => { this.handleGetTemplateDetail(v);   },
            error: (e)                     => { this.handleError(e) ;               }
          });
    }
  }

  private handleGetTemplateDetail(response: LMFacadeReportTemplateDetail) {
    this.lmBusyIndicatorService.hideBusyIndicator();

    if (response.reportType) {
      this.displayTemplateDetail.set(response);

      if (this.displayTemplateDetail().asOfDateData) {
        const asOfDateDisplay = this.lmFormatUtilService.yyyymmddToDisplay(this.displayTemplateDetail().asOfDateData);
        this.asOfDateLabel.set(Soho.Locale.translate('AsOfDateReportType', asOfDateDisplay));
      } else {
        this.asOfDateLabel.set('');
      }

      this.calculateMaxHeight();
      this.initialModuleAccordion();
    } else {
      this.handleError(response);
    }
  }

  private initialModuleAccordion() {
    if (this.accordionComponent) {

      this.accordionComponent?.updated();
      this.ngZone.runOutsideAngular( () => {
        setTimeout(() => {
          if (this.displayTemplateDetail() && this.accordionComponent) {
            if (this.showFields()) {
              this.accordionComponent.expand('fields');
            } else if (this.showDimensions()) {
              this.accordionComponent.expand('dimensions');
            } else if (this.showMeasures()) {
              this.accordionComponent.expand('measures');
            } else if (this.showFilter()) {
              this.accordionComponent.expand('filter');
            } else if (this.showSort()) {
              this.accordionComponent.expand('sort');
            }
          }
        });
      });
    }
  }

  private handleError(error: any) {
    this.lmBusyIndicatorService.hideBusyIndicator();
    this.lmErrorUtilService.handleUnexpectedError(error);
  }

  onStartReportClicked() {
    if (this.reportTemplateDetail) {
      const listResource = new ListResource({ resourceId: this.reportTemplateDetail.template.businessView + '.' + this.reportTemplateDetail.template.name});
      this.lmFacadeService.copyTemplate(listResource, this.reportTemplateDetail.template.userFolder);
    }
  }

  calculateMaxHeight(): void {
    let sections = 0;

    // @ts-ignore
    if (this.displayTemplateDetail()?.fields?.length > 0) {
      sections++;
      this.showFields.set(true);
    }
    // @ts-ignore
    if (this.displayTemplateDetail()?.dimensions?.length > 0) {
      sections++;
      this.showDimensions.set(true);
    }
    // @ts-ignore
    if (this.displayTemplateDetail()?.measures?.length > 0) {
      sections++;
      this.showMeasures.set(true);
    }
    if (this.displayTemplateDetail()?.listFilter?.length > 0 || this.displayTemplateDetail()?.reportFilter?.length > 0) {
      sections++;
      this.showFilter.set(true);
    }
    if (this.displayTemplateDetail()?.sortBy) {
      sections++;
      this.showSort.set(true);
    }

    if (sections === 2)
      this.heightClass.set('lm-two-panes');
    if (sections === 3)
      this.heightClass.set('lm-three-panes');
    if (sections === 4)
      this.heightClass.set('lm-four-panes');

  }
}
